// Seu arquivo Preparation.tsx, agora refatorado

import Loading from "@/common/components/ui/loading/Loading";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import { formatDateAndTime } from "@/common/helpers/date.helpers";
import {
  getWorkOrders,
  markWorkOrdersAsPayed,
} from "@/common/services/workOrder.api";
import { IEmployee } from "@/types/employee.type";
import { IWorkOrder } from "@/types/workorder.types";
import { Suspense, useEffect, useState } from "react";
import GenericTable from "@/common/components/ui/table/GenericTable";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { IPurchaseRequestor } from "@/types/purchaseRequestor.type";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { useUser } from "@/common/services/users.api";
import { getAllPurchaseRequestors } from "@/common/services/purchaseRequestor.api";
import { createPurchaseOrder } from "@/common/services/purchaseOrder.api";
import {
  emptyPurchaseOrder,
  PURCHASEORDER_STATUS,
  TYPE_PAYMENT,
} from "@/types/purchaseOrder.types";
import { getCode } from "@/lib/utils";

type PreparationDashboardProps = {
  consultant: IEmployee;
};

function Preparation(props: PreparationDashboardProps) {
  const { consultant } = props;
  const [page, setPage] = useState<number>(1);
  const [workOrders, setWorkOrders] = useState<IWorkOrder[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isBilling, setIsBilling] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: ["false"],
    orderBy: "-createdAt",
  });
  const [requestors, setRequestors] = useState<IPurchaseRequestor[]>([]);
  const { notifySuccess, notifyError } = useToastContext();
  const user = useUser();

  const CATEGORY_COMMISSION = {
    id: "f3dabaa9-8914-4c19-b484-4fab188521bf",
    name: "Comissão",
  };

  const COST_CENTER_COMMERCIAL = {
    id: "ad216570-4aac-41d5-ab22-1252182384da",
    name: "COMERCIAL",
  };

  useEffect(() => {
    async function loadRequestors() {
      const result = await getAllPurchaseRequestors();
      if (result.ok) setRequestors(result.data);
    }
    loadRequestors();
  }, []);
  useEffect(() => {
    async function loadWorkOrders() {
      setIsLoading(true);
      const result = await getWorkOrders({
        page,
        itemsPerPage: 10,
        search: filters.search,
        status: filters.status,
        mechanic: consultant.name,
        payed: filters.payed,
      });

      if (result.ok) {
        if (Array.isArray(result.data)) {
          setWorkOrders(result.data);
        } else {
          setWorkOrders([]);
        }

        if (typeof result.totalItems === "number") {
          setTotalItems(result.totalItems);
        } else {
          setTotalItems(0);
        }
      }
      setIsLoading(false);
    }
    loadWorkOrders();
  }, [filters, page, consultant.name]);

  const headers = {
    licensePlate: "Placa",
    createdAt: "Data de Criação",
    payed: "Pago",
    kitPPF: "Kit PPF",
    tracker: "Rastreador",
    cellphoneSupport: "Sup. Celular",
    engineProtection: "Prot. Carenagem",
    cellphoneCharger: "Carregador Celular",
  };

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setPage(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const handleBillAll = async () => {
    setIsBilling(true);
    const freshUnpaidWorkOrdersResult = await getWorkOrders({
      mechanic: consultant.name,
      status: "false",
      itemsPerPage: 9999,
    });

    if (!freshUnpaidWorkOrdersResult.ok) {
      notifyError("Não foi possível buscar as OSs atualizadas para faturar.");
      setIsBilling(false);
      return;
    }
    const commissionableItems = freshUnpaidWorkOrdersResult.data.filter(
      (wo) => wo.kitPPF
    );

    if (commissionableItems.length === 0) {
      notifyError("Não há ordens de serviço com Kit PPF para faturar.");
      setIsBilling(false);
      return;
    }

    const totalCommission = commissionableItems.length * 10;

    const requestor = requestors.find(
      (req) => req.employeeEmail === user.email
    );

    if (!requestor) {
      notifyError(
        "Usuário solicitante não encontrado. Verifique suas permissões."
      );
      setIsBilling(false);
      return;
    }

    const defaultValues = emptyPurchaseOrder();
    const now = new Date();

    const purchaseOrder = {
      ...defaultValues,
      isCounted: false,
      description: `Pagamento de comissão - ${consultant.name}`,
      companyId: consultant.companyId,
      requestorId: requestor._id as string,
      requestorName: requestor.name as string,
      requestorEmail: requestor.employeeEmail as string,
      code: getCode().toString(),
      status: PURCHASEORDER_STATUS.PENDING,
      supplierId: consultant._id ? consultant._id : "",
      supplierName: consultant.name,
      financial: {
        ...defaultValues.financial,
        categoryId: CATEGORY_COMMISSION.id,
        categoryName: CATEGORY_COMMISSION.name,
        costCenterId: COST_CENTER_COMMERCIAL.id,
        costCenterName: COST_CENTER_COMMERCIAL.name,
        typePayment: TYPE_PAYMENT.Pix,
        value: totalCommission,
        expectedDate: now,
        instalment: [
          {
            installmentNumber: "1",
            value: totalCommission,
            dueDate: now,
          },
        ],
      },
      itens: commissionableItems.map((wo) => ({
        description: `Comissão Kit PPF - OS: ${wo.workOrderCode} | Placa: ${wo.licensePlate}`,
        qtd: 1,
        value: "10.00",
      })),
      createdAt: now,
    };

    try {
      const result = await createPurchaseOrder(purchaseOrder, true);

      if (result.ok) {
        notifySuccess("Ordem de Compra para comissão gerada com sucesso!");
        const idsToUpdate = commissionableItems.map(
          (item) => item._id as string
        );
        const updateResult = await markWorkOrdersAsPayed(idsToUpdate);

        if (updateResult.ok) {
          notifySuccess(
            `${updateResult.data.modifiedCount} OS(s) foram marcadas como pagas.`
          );

          setWorkOrders((currentWorkOrders) =>
            currentWorkOrders.filter(
              (wo) => !idsToUpdate.includes(wo._id as string)
            )
          );
        } else {
          notifyError(
            "A OC foi criada, mas falhou ao atualizar o status das OSs."
          );
        }
      } else {
        notifyError(result.message || "Erro ao gerar Ordem de Compra.");
      }
    } catch (e) {
      notifyError("Ocorreu um erro inesperado ao gerar a Ordem de Compra.");
    } finally {
      setIsBilling(false);
    }
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="tabs-border-line-commission">
        <div className="tabs-item-style-commission">
          <div className="table-section">
            <TableFilters
              dashboardHeader={`Ordens de Serviço de ${consultant.name}`}
              placeholder="Buscar por placa..."
              statusOptions={{ false: "Não Pago", true: "Pago" }}
              onSearch={handleFilterChange}
              tableData={workOrders}
            />
            <div className="my-4 px-4 sm:px-0 h-10">
              <button
                onClick={handleBillAll}
                className="bg-redPrimary text-white font-bold py-2 px-4 rounded hover:bg-red-700 transition duration-300 disabled:bg-gray-400"
              >
                {isBilling ? "Faturando..." : `Faturar Tudo `}
              </button>
            </div>
            <GenericTable
              headers={headers}
              data={workOrders}
              renders={{
                createdAt: formatDateAndTime,
                payed: (value) =>
                  value ? (
                    <span className="text-green-600 font-semibold">Sim</span>
                  ) : (
                    <span className="text-red-600 font-semibold">Não</span>
                  ),
                kitPPF: (value) => (value ? "Sim" : "Não"),
                tracker: (value) => (value ? "Sim" : "Não"),
                cellphoneSupport: (value) => (value ? "Sim" : "Não"),
                engineProtection: (value) => (value ? "Sim" : "Não"),
                cellphoneCharger: (value) => (value ? "Sim" : "Não"),
              }}
            />
            {isLoading && <Loading />}
            <TableFooter
              page={page}
              totalItems={totalItems}
              itemsPerPage={10}
              goToPage={setPage}
            />
          </div>
        </div>
      </div>
    </Suspense>
  );
}

export default Preparation;
